import { Dot } from "@/components/Dot";
import { EvoNodeVariant } from "@/design-system/Evolution/EvoNode";
import { TextButton } from "@/design-system/TextButton/TextButton";
import { cn } from "@/lib/utils";
import { evolutionChainTotals } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { ReactNode, useMemo, useState } from "react";

import { EvoDataRow } from "../EvoDataRow";
import { EvoChainFromIds, useEvolutionsById } from "./EvoChainFromIds";
import { EvoChainSheet } from "./EvoChainSheet";

export interface EvolutionChainCardProps {
  evolutionIds: number[];
  pathwayPath?: number[][];
  /** Defaults to "Evolution Chain". */
  title?: string;
  /** Right-hand slot of the header row. */
  action?: ReactNode;
  /** Shown where the chain starts while it is empty. */
  emptyHint?: ReactNode;
  /** A "Chain Info" action that opens the sheet on the first step. */
  detailsAction?: boolean;
  /** "editable" adds the × that removes a step and everything after it. */
  variant?: Extract<EvoNodeVariant, "readonly" | "editable">;
  onRemoveStep?: (index: number) => void;
  layout?: "wrap" | "scroll";
  /** Fade steps in as they are added, as the builder does. */
  animate?: boolean;
  /** The sticky, header-less bar the builder shows once scrolled past. */
  compact?: boolean;
  className?: string;
}

/**
 * The boxed "Evolution Chain (n)": what the whole chain costs and takes, the
 * chain as chips that open the chain sheet, and an action. Read-only on the
 * player page, editable in the builder.
 *
 * Styled with the 2.0 tokens: a surface card on the page background.
 */
export const EvolutionChainCard = ({
  evolutionIds,
  pathwayPath,
  title,
  action,
  emptyHint,
  detailsAction = false,
  variant = "readonly",
  onRemoveStep,
  layout = "wrap",
  animate = false,
  compact = false,
  className,
}: EvolutionChainCardProps) => {
  const { t } = useTranslation("evolutions");
  const totals = useChainTotals(evolutionIds);
  const [openStep, setOpenStep] = useState<number | null>(null);

  const headerAction =
    action ??
    (detailsAction && (
      <TextButton onClick={() => setOpenStep(0)}>
        {t("chain.detailedInfo")}
      </TextButton>
    ));

  return (
    <div
      className={cn(
        "flex flex-col gap-1 rounded-[20px] border border-edge-strong bg-surface",
        compact ? "px-3 py-1.5" : "px-4 py-3",
        className,
      )}
    >
      {!compact && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="text-sm font-semibold text-foreground">
              {title ?? t("chain.title")}{" "}
              <span className="font-normal text-foreground-muted">
                ({evolutionIds.length})
              </span>
            </h3>
            {evolutionIds.length > 0 && (
              <>
                <Dot color="bg-edge-strongest" />
                <div className="text-foreground-body [&>div]:w-auto">
                  <EvoDataRow
                    evolutionData={{
                      cost: totals.cost,
                      requiredGames: totals.games,
                    }}
                    size="medium"
                    variant="inherit"
                  />
                </div>
              </>
            )}
          </div>
          {headerAction}
        </div>
      )}
      <div className="flex items-center gap-x-3">
        {evolutionIds.length === 0 && emptyHint}
        <EvoChainFromIds
          evolutionIds={evolutionIds}
          pathwayPath={pathwayPath}
          variant={variant}
          onOpenStep={setOpenStep}
          onRemoveStep={onRemoveStep}
          layout={layout}
          animate={animate}
          reserveScrollbarSpace={!compact}
        />
        {compact && headerAction && (
          <div className="shrink-0">{headerAction}</div>
        )}
      </div>
      <EvoChainSheet
        evolutionIds={evolutionIds}
        pathwayPath={pathwayPath}
        isOpen={openStep !== null}
        onClose={() => setOpenStep(null)}
        initialIndex={openStep ?? 0}
      />
    </div>
  );
};

/**
 * What the chain adds up to, against the loaded catalogue.
 */
const useChainTotals = (evolutionIds: number[]) => {
  const resolve = useEvolutionsById();
  return useMemo(
    () => evolutionChainTotals({ evolutionIds, resolve }),
    [evolutionIds, resolve],
  );
};
